import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function Navbar() {
    // menuOpen: Current state(true, false), setMenuOpen: Change state(true => false, false => true).
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        // Function looks for click event
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        // Function looks for scroll event
        const handleScroll = () => {
            setMenuOpen(false);
        };
        
        // Starts listening for events when menu is open.
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("scroll", handleScroll);
        }

        // Stop listening to events
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("scroll", handleScroll);
        };
    }, [menuOpen]);


    return (
        <header className="navigationBar h-20 fixed w-full shadow-sm backdrop-blur-lg bg-slate-950/50 z-50">
            {/* Main container */}
            <nav className="navContent flex h-full
                            w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] xl:w-[75%] 
                            py-9 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12
                            mx-auto 
                            items-center justify-between z-50 ">

                {/* Logo */}
                <div className="logo 
                                sm:w-8 sm:h-8 
                                md:w-10 md:h-10 
                                lg:w-12 lg:h-12
                                xl:w-16 xl:h-16 
                                2xl:w-20 2xl:h-20
                                flex justify-start items-center">
                    <Link to='/'>
                        <svg width="50" height="50" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M28.9735 26.3448H17.7852L15.8966 30.7586H12.4483L16.37 21.5933L21.6552 9.24133H25.1035L34.3104 30.7586H30.8621L28.9735 26.3448ZM27.4981 22.8965L23.3794 13.2708L19.2607 22.8965H27.4981Z" fill="url(#paint0_linear_183_2)"/>
                            <path d="M10.3449 30.7586V21.6551H14.2414L15.7242 18.2069H10.3449V12.6896H18.0793L19.555 9.24133H6.89661V30.7586H10.3449Z" fill="url(#paint1_linear_183_2)"/>
                            <defs>
                                <linearGradient id="paint0_linear_183_2" x1="20.6035" y1="9.24133" x2="20.6035" y2="30.7586" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#F1F5F9"/>
                                    <stop offset="1" stopColor="#64748B"/>
                                </linearGradient>
                                <linearGradient id="paint1_linear_183_2" x1="20.6035" y1="9.24133" x2="20.6035" y2="30.7586" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#F1F5F9"/>
                                    <stop offset="1" stopColor="#64748B"/>
                                </linearGradient>
                            </defs>
                        </svg>
                    </Link>
                </div>
                
                {/* Desktop Menu */}
                <ul className="hidden sm:flex gap-10
                               text-sm sm:text-base md:text-md lg:text-lg">
                    <li>
                        <Link to='/' state={{ scrollTo: "workSection" }}>
                            <p className='hover:text-gray-400'>What I’ve Built</p>
                        </Link>
                    </li>
                    <li>
                        <Link to='/' state={{ scrollTo: "skillSection" }}>
                            <p className='hover:text-gray-400'>Toolkit</p>
                        </Link>
                    </li>
                    <li>
                        <Link to='/' state={{ scrollTo: "aboutSection" }}>
                            <p className='hover:text-gray-400'>About</p>
                        </Link>
                    </li>
                    <li>
                        <a href="mailto:fa01.arias@gmail.com">
                            <p className='hover:text-gray-400'>Say Hey</p>
                        </a>
                    </li>
                </ul>

                {/* Mobile Menu Button*/}
                <button
                    className="sm:hidden text-sm sm:text-base flex items-center"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
                    aria-expanded={menuOpen}
                    aria-controls="mobile-menu"
                >
                    <motion.span
                        initial={{ rotate: 0, scale: 1 }}
                        animate={{ rotate: menuOpen ? 180 : 0, scale: menuOpen ? 1.1 : 1 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        {menuOpen ? (
                            <motion.div
                                key="close-icon"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.2 }}
                            >
                                <X size={28} />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="menu-icon"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Menu size={28} />
                            </motion.div>
                        )}
                    </motion.span>
                </button>


                {/* Mobile Menu*/}
                <AnimatePresence>
                    {menuOpen && (
                        <motion.ul id="mobile-menu" ref={menuRef}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className="z-10 fixed top-20 right-4 bg-gray-700 divide-y divide-gray-100 rounded-lg shadow-sm w-44  sm:hidden">
                            <li>
                                <Link to='/' state={{ scrollTo: "workSection" }}>
                                    <p className='block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'>What I’ve Built</p>
                                </Link>
                            </li>
                            <li>
                                <Link to='/' state={{ scrollTo: "skillSection" }}>
                                    <p className='block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'>Toolkit</p>
                                </Link>
                            </li>
                            <li>
                                <Link to='/' state={{ scrollTo: "aboutSection" }}>
                                    <p className='block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'>About</p>
                                </Link>
                            </li>
                            <li>
                                <a href="mailto:fa01.arias@gmail.com">
                                    <p className='block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'>Say Hey</p>
                                </a>
                            </li>
                        </ motion.ul>
                    )}
                </AnimatePresence>
                
            </nav>
        </header>
    )
  }

export default Navbar